import { formatARS } from '@gl/shared';
import { LayoutGrid, ListChecks, Search, Store } from 'lucide-react';
import { useMemo, useState } from 'react';
import { Controller, useWatch, type UseFormReturn } from 'react-hook-form';
import { Spinner } from '@/components/FullScreenLoader';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { errorMessage } from '@/lib/api';
import { useCatalog } from '../hooks';
import type { CatalogCategory, CatalogItem } from '../types';
import { Checkbox } from '../ui/checkbox';
import { OptionCards } from '../ui/option-cards';
import type { WizardValues } from '../wizard-form';

function itemPriceLabel(item: CatalogItem): string {
  if (item.variants.length === 0) return formatARS(item.priceCents);
  const prices = item.variants.map((v) => v.priceCents);
  const min = Math.min(...prices);
  const max = Math.max(...prices);
  return min === max ? formatARS(min) : `${formatARS(min)} – ${formatARS(max)}`;
}

function toggle(list: string[], id: string, on: boolean): string[] {
  return on ? [...new Set([...list, id])] : list.filter((x) => x !== id);
}

export function useActiveCategories(localId: string) {
  const catalog = useCatalog(localId);
  const categories: CatalogCategory[] = useMemo(
    () =>
      (catalog.data?.categories ?? [])
        .map((c) => ({ ...c, items: c.items.filter((i) => i.isActive) }))
        .sort((a, b) => a.sortOrder - b.sortOrder),
    [catalog.data],
  );
  return { catalog, categories };
}

export function ScopeStep({ form, localId }: { form: UseFormReturn<WizardValues>; localId: string }) {
  const { catalog, categories } = useActiveCategories(localId);
  const scopeMode = useWatch({ control: form.control, name: 'scopeMode' });
  const { errors } = form.formState;

  return (
    <div className="grid gap-5">
      <Controller
        control={form.control}
        name="scopeMode"
        render={({ field }) => (
          <OptionCards
            name="Alcance"
            value={field.value}
            onChange={field.onChange}
            options={[
              { value: 'all', label: 'Todo el menú', description: 'Todos los productos activos', icon: <Store /> },
              { value: 'categories', label: 'Categorías', description: 'Una o varias categorías', icon: <LayoutGrid /> },
              { value: 'items', label: 'Productos', description: 'Elegí uno por uno', icon: <ListChecks /> },
            ]}
          />
        )}
      />

      {scopeMode !== 'all' &&
        (catalog.isPending ? (
          <div className="flex justify-center py-6">
            <Spinner className="size-5 text-muted-foreground" />
          </div>
        ) : catalog.error ? (
          <p className="text-sm text-destructive">No pudimos cargar el menú: {errorMessage(catalog.error)}</p>
        ) : categories.length === 0 ? (
          <p className="text-sm text-muted-foreground">Todavía no cargaste productos en el menú.</p>
        ) : scopeMode === 'categories' ? (
          <Controller
            control={form.control}
            name="categoryIds"
            render={({ field }) => (
              <CategoryPicker
                categories={categories}
                value={field.value}
                onChange={field.onChange}
                error={errors.categoryIds?.message}
              />
            )}
          />
        ) : (
          <Controller
            control={form.control}
            name="itemIds"
            render={({ field }) => (
              <ItemPicker categories={categories} value={field.value} onChange={field.onChange} error={errors.itemIds?.message} />
            )}
          />
        ))}

      <Controller
        control={form.control}
        name="includeVariants"
        render={({ field }) => (
          <div className="flex items-start justify-between gap-4 rounded-lg border p-3">
            <div className="grid gap-0.5">
              <Label htmlFor="include-variants">Incluir variantes</Label>
              <p className="text-xs text-muted-foreground">
                Actualiza también los tamaños y presentaciones (1/4 kg, 1 bocha, etc.). Si lo apagás, solo cambia el precio
                base de cada producto.
              </p>
            </div>
            <Switch id="include-variants" checked={field.value} onCheckedChange={field.onChange} />
          </div>
        )}
      />
    </div>
  );
}

function CategoryPicker({
  categories,
  value,
  onChange,
  error,
}: {
  categories: CatalogCategory[];
  value: string[];
  onChange: (v: string[]) => void;
  error?: string;
}) {
  const allSelected = categories.every((c) => value.includes(c.id));
  return (
    <div className="grid gap-2">
      <div className="flex items-center justify-between">
        <p className="text-sm font-medium">{value.length === 0 ? 'Elegí las categorías' : `${value.length} elegidas`}</p>
        <Button type="button" variant="ghost" size="sm" onClick={() => onChange(allSelected ? [] : categories.map((c) => c.id))}>
          {allSelected ? 'Ninguna' : 'Todas'}
        </Button>
      </div>
      <div className="grid gap-1 rounded-lg border p-1 sm:grid-cols-2">
        {categories.map((c) => {
          const id = `cat-${c.id}`;
          return (
            <label key={c.id} htmlFor={id} className="flex min-h-11 cursor-pointer items-center gap-3 rounded-md px-2 hover:bg-accent">
              <Checkbox id={id} checked={value.includes(c.id)} onCheckedChange={(on) => onChange(toggle(value, c.id, on === true))} />
              <span className="flex-1 text-sm">
                {c.name}
                {!c.isActive && <span className="text-muted-foreground"> (oculta)</span>}
              </span>
              <span className="text-xs text-muted-foreground">
                {c.items.length === 1 ? '1 producto' : `${c.items.length} productos`}
              </span>
            </label>
          );
        })}
      </div>
      {error && <p className="text-xs text-destructive">{error}</p>}
    </div>
  );
}

function ItemPicker({
  categories,
  value,
  onChange,
  error,
}: {
  categories: CatalogCategory[];
  value: string[];
  onChange: (v: string[]) => void;
  error?: string;
}) {
  const [search, setSearch] = useState('');
  const term = search.trim().toLocaleLowerCase('es');
  const filtered = categories
    .map((c) => ({
      ...c,
      items: term ? c.items.filter((i) => i.name.toLocaleLowerCase('es').includes(term)) : c.items,
    }))
    .filter((c) => c.items.length > 0);

  return (
    <div className="grid gap-2">
      <div className="relative">
        <Search className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Buscar producto"
          className="pl-9"
          aria-label="Buscar producto"
        />
      </div>
      <div className="flex items-center justify-between">
        <p className="text-sm font-medium">{value.length === 0 ? 'Elegí los productos' : `${value.length} elegidos`}</p>
        {value.length > 0 && (
          <Button type="button" variant="ghost" size="sm" onClick={() => onChange([])}>
            Limpiar
          </Button>
        )}
      </div>
      <div className="max-h-[45vh] overflow-y-auto rounded-lg border p-1">
        {filtered.length === 0 ? (
          <p className="p-3 text-sm text-muted-foreground">No hay productos que coincidan con “{search}”.</p>
        ) : (
          filtered.map((c) => (
            <div key={c.id} className="mb-1">
              <p className="sticky top-0 z-10 bg-background px-2 pt-2 pb-1 text-xs font-semibold tracking-wide text-muted-foreground uppercase">
                {c.name}
              </p>
              {c.items.map((i) => {
                const id = `item-${i.id}`;
                return (
                  <label
                    key={i.id}
                    htmlFor={id}
                    className="flex min-h-11 cursor-pointer items-center gap-3 rounded-md px-2 hover:bg-accent"
                  >
                    <Checkbox id={id} checked={value.includes(i.id)} onCheckedChange={(on) => onChange(toggle(value, i.id, on === true))} />
                    <span className="flex-1 text-sm">
                      {i.name}
                      {i.variants.length > 0 && <span className="text-muted-foreground"> · {i.variants.length} variantes</span>}
                    </span>
                    <span className="text-xs text-muted-foreground tabular-nums">{itemPriceLabel(i)}</span>
                  </label>
                );
              })}
            </div>
          ))
        )}
      </div>
      {error && <p className="text-xs text-destructive">{error}</p>}
    </div>
  );
}
